import { ref } from 'vue'
import { supabase } from '@/lib/supabase'
import { useAppContextStore } from '@/stores/appContext'
import { useDailyCompletionsStore } from '@/stores/dailyCompletions'
import { useAnalytics } from '@/composables/useAnalytics'

/**
 * « Commence avec {prénom} », après le paywall multi-enfants refermé (HARMONIA-93).
 *
 * Le parcours relevé en 3.0.3 : onboarding complet, un enfant prêt, puis « Ajouter
 * un enfant » dans le sélecteur, le paywall, fermé en quelques secondes, et plus
 * aucune session. Ce sont des familles de deux enfants ou plus qui découvrent la
 * limite du gratuit juste après avoir tout configuré. Le paywall est légitime ;
 * c'est la sortie qui les perd : rien ne leur dit qu'elles peuvent commencer avec
 * l'enfant déjà créé.
 *
 * Cette carte remplace la question « pourquoi tu refermes ? » dans ce cas-là
 * seulement : foyer qui n'a encore rien validé, paywall ouvert depuis le
 * sélecteur. Deux modales d'affilée au moment le plus fragile coûtent plus que la
 * raison, qui reste collectée chez les foyers activés.
 */

/** La seule porte qui mène ici : le « + » du sélecteur d'enfant. */
export const START_WITH_ONE_SURFACE = 'child_picker_add'

// État partagé, comme la question de raison : la carte est montée une fois dans
// App.vue, et c'est un composable sans écran qui décide de l'ouvrir.
const isOpen = ref(false)
const childId = ref<string | null>(null)
const childName = ref('')
const surface = ref('')

// `true` au repos : rien à logguer tant qu'aucune carte n'est ouverte. Même garde
// anti-double-tir que la feuille des raisons, `did-dismiss` pouvant repasser.
let outcomeLogged = true
let accepted = false

/**
 * « Ce foyer a-t-il déjà validé une tâche, une seule fois ? »
 *
 * Tri-état volontaire : `null` quand on ne sait pas. Dans le doute on ne montre
 * pas la carte et la question de raison garde sa place, ce qui est le
 * comportement d'avant : se tromper dans ce sens ne coûte rien.
 *
 * « Une seule fois » compte aussi une tâche cochée puis décochée : le parent qui
 * a suivi la carte coche souvent une tâche pour voir, puis la décoche parce que
 * l'enfant n'est pas là. Il a compris le principe, on ne lui repropose pas.
 * `daily_completions` ne le dit plus (son compteur redescend à 0), le registre
 * des pièces si : la ligne `task_completion` reste, la décoche en ajoute une
 * autre (`task_uncomplete`).
 *
 * Les complétions du jour déjà en mémoire d'abord (zéro requête), puis deux
 * sondes de tête en parallèle : on ne lit que des nombres de lignes. La
 * seconde rattrape une tâche à 0 point, qui ne verse aucune pièce.
 */
export async function hasEverValidatedTask(childIds: string[]): Promise<boolean | null> {
  if (childIds.length === 0) return null
  try {
    const ids = new Set(childIds)
    for (const row of useDailyCompletionsStore().completions.values()) {
      if (ids.has(row?.child_id) && (row?.tasks_completed ?? 0) > 0) return true
    }
    const [ledger, completions] = await Promise.all([
      supabase
        .from('coin_transactions')
        .select('id', { count: 'exact', head: true })
        .in('child_id', childIds)
        .eq('source', 'task_completion'),
      supabase
        .from('daily_completions')
        .select('id', { count: 'exact', head: true })
        .in('child_id', childIds)
        .gt('tasks_completed', 0),
    ])
    if (ledger.error) throw ledger.error
    if (completions.error) throw completions.error
    return (ledger.count ?? 0) > 0 || (completions.count ?? 0) > 0
  } catch (e) {
    console.warn('[useStartWithOne] activation probe failed:', e)
    return null
  }
}

/**
 * Appelé à la fermeture du paywall sans achat, AVANT la question de raison.
 * Renvoie true quand la carte a pris la place de la question.
 */
export async function maybeOfferStartWithOne(from: string): Promise<boolean> {
  if (from !== START_WITH_ONE_SURFACE) return false

  const appContext = useAppContextStore()
  if (appContext.isPaidPremium) return false

  const children = (appContext.availableChildren ?? []) as Array<{ id: string; name?: string }>
  // L'enfant que le parent regarde, sinon le premier : un foyer gratuit n'en a
  // qu'un, c'est celui qu'il vient de configurer.
  const child = children.find((c) => c.id === appContext.currentChildId) ?? children[0]
  if (!child) return false

  if ((await hasEverValidatedTask(children.map((c) => c.id))) !== false) return false

  childId.value = child.id
  childName.value = child.name ?? ''
  surface.value = from
  accepted = false
  outcomeLogged = false
  isOpen.value = true
  void useAnalytics().logStartWithOneShown(from, child.id)
  return true
}

export function useStartWithOne() {
  /** « Valider une première tâche » : la navigation est faite par la carte. */
  function accept() {
    accepted = true
    isOpen.value = false
  }

  /** « Plus tard », balayage, fond touché : même issue. */
  function later() {
    accepted = false
    isOpen.value = false
  }

  /**
   * Fin d'animation : seul point d'écriture de l'issue. Renvoie true quand le
   * parent a accepté, pour que la carte navigue une fois partie de l'écran, et
   * une seule fois.
   */
  function onDismissed(): boolean {
    if (outcomeLogged) return false
    outcomeLogged = true
    const analytics = useAnalytics()
    if (accepted) void analytics.logStartWithOneAccepted(surface.value, childId.value)
    else void analytics.logStartWithOneDismissed(surface.value, childId.value)
    return accepted
  }

  return { isOpen, childId, childName, accept, later, onDismissed }
}

/** Remet l'état à zéro. Réservé aux tests. */
export function __resetStartWithOne() {
  isOpen.value = false
  childId.value = null
  childName.value = ''
  surface.value = ''
  outcomeLogged = true
  accepted = false
}
